import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Plus, Trash2, ListChecks, Sparkles } from "lucide-react";
import { T } from "@/lib/testIds";

const CHANNELS = ["email", "whatsapp"];

const DEFAULT_TEMPLATE =
  "Hi {client_name}, this is a friendly reminder from {business_name}. " +
  "Your payment of {currency} {amount} for {product_name} is due on {due_date}. " +
  "View or pay online: {portal_link}\n{payment_links}";

function RuleCard({ rule, onSave, onDelete, saving }) {
  const [state, setState] = useState({
    ...rule,
    daysStr: (rule.days_before || []).join(", "),
    segStr: (rule.segment_filter || []).join(", "),
  });

  useEffect(() => {
    setState({
      ...rule,
      daysStr: (rule.days_before || []).join(", "),
      segStr: (rule.segment_filter || []).join(", "),
    });
  }, [rule]);

  const setF = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const toggleChannel = (c) =>
    setState((s) => ({
      ...s,
      channels: s.channels.includes(c) ? s.channels.filter((x) => x !== c) : [...s.channels, c],
    }));

  const save = () => {
    const days_before = state.daysStr
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => Number.isFinite(n) && n >= 0);
    const segment_filter = state.segStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    onSave(state.id, {
      name: state.name,
      days_before,
      segment_filter,
      channels: state.channels,
      message_template: state.message_template,
      active: state.active,
    });
  };

  return (
    <div className="border co-hairline bg-white">
      <div className="p-5 border-b co-hairline flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <ListChecks className="h-4 w-4 text-[#4338CA]" strokeWidth={1.75} />
          <Input
            data-testid={T.ruleNameInput}
            value={state.name}
            onChange={(e) => setF("name", e.target.value)}
            className="h-9 max-w-xs font-semibold"
          />
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={state.active}
            onCheckedChange={(v) => setF("active", v)}
            data-testid={`rule-active-${state.id}`}
          />
          <span className="text-xs font-semibold">{state.active ? "Active" : "Paused"}</span>
          <button
            onClick={() => onDelete(state.id)}
            className="p-2 hover:bg-[#FEF2F2] rounded-sm transition-colors duration-200"
            data-testid={`${T.ruleDeleteBtn}-${state.id}`}
            title="Delete rule"
          >
            <Trash2 className="h-4 w-4 text-[#B91C1C]" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <Label className="co-label">Days before due</Label>
          <Input
            data-testid={T.ruleDays}
            value={state.daysStr}
            onChange={(e) => setF("daysStr", e.target.value)}
            className="mt-2 font-mono"
            placeholder="7, 3, 0"
          />
          <div className="text-xs text-[#52525B] mt-1.5">
            Comma-separated. <span className="font-mono">0</span> = on due date.
          </div>
        </div>

        <div>
          <Label className="co-label">Applies to segments (empty = all)</Label>
          <Input
            data-testid={T.ruleSegmentFilter}
            value={state.segStr}
            onChange={(e) => setF("segStr", e.target.value)}
            className="mt-2"
            placeholder="e.g. gym-premium, vip"
          />
          <div className="text-xs text-[#52525B] mt-1.5">
            Rule fires only for clients with any of these tags.
          </div>
        </div>

        <div className="lg:col-span-2">
          <Label className="co-label">Channels</Label>
          <div className="mt-3 flex gap-4" data-testid={T.ruleChannels}>
            {CHANNELS.map((c) => (
              <label
                key={c}
                className={`flex items-center gap-2 border co-hairline px-4 py-2.5 rounded-sm cursor-pointer transition-colors duration-200 ${
                  state.channels.includes(c) ? "border-[#4338CA] bg-[#ECECFE]" : "hover:bg-[#FAFAFA]"
                }`}
                data-testid={`rule-channel-${state.id}-${c}`}
              >
                <Checkbox checked={state.channels.includes(c)} onCheckedChange={() => toggleChannel(c)} />
                <span className="text-sm font-semibold capitalize">{c}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2">
          <Label className="co-label">Message template</Label>
          <Textarea
            data-testid={T.ruleTemplate}
            value={state.message_template}
            onChange={(e) => setF("message_template", e.target.value)}
            className="mt-2 font-mono text-xs"
            rows={7}
          />
          <div className="text-xs text-[#52525B] mt-1.5">
            Placeholders:{" "}
            <span className="font-mono">
              {"{client_name} {business_name} {amount} {currency} {product_name} {due_date} {portal_link} {payment_links}"}
            </span>
          </div>
        </div>

        <div className="lg:col-span-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid={`${T.ruleSave}-${state.id}`}
            className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
          >
            <Save className="h-4 w-4" strokeWidth={2} /> Save rule
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReminderRules() {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [newRule, setNewRule] = useState({
    name: "", daysStr: "7, 3, 0", segStr: "", templateId: "", template: DEFAULT_TEMPLATE, channels: ["email", "whatsapp"],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.get("/reminder-rules"),
        api.get("/reminder-templates"),
      ]);
      setRules(r.data);
      setTemplates(t.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (id, patch) => {
    setSaving(true);
    try {
      const r = await api.patch(`/reminder-rules/${id}`, patch);
      setRules((rs) => rs.map((x) => (x.id === id ? r.data : x)));
      toast.success("Rule saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reminder rule?")) return;
    try {
      await api.delete(`/reminder-rules/${id}`);
      setRules((rs) => rs.filter((x) => x.id !== id));
      toast.success("Rule deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const days_before = newRule.daysStr
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n >= 0);
      const segment_filter = newRule.segStr
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const r = await api.post("/reminder-rules", {
        name: newRule.name || "New rule",
        days_before: days_before.length ? days_before : [7, 3, 0],
        segment_filter,
        channels: newRule.channels && newRule.channels.length ? newRule.channels : ["email", "whatsapp"],
        message_template: newRule.template || DEFAULT_TEMPLATE,
        active: true,
      });
      setRules((rs) => [...rs, r.data]);
      setNewRule({ name: "", daysStr: "7, 3, 0", segStr: "", templateId: "", template: DEFAULT_TEMPLATE, channels: ["email", "whatsapp"] });
      setOpen(false);
      toast.success("Rule created");
    } catch {
      toast.error("Create failed");
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setNewRule({
      name: t.name,
      daysStr: (t.days_before || []).join(", "),
      segStr: (t.suggested_segments || []).join(", "),
      templateId: id,
      template: t.message_template,
      channels: t.channels || ["email", "whatsapp"],
    });
  };

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="co-label">Automation</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">
            Reminder rules
          </h1>
        </div>
        <button
          data-testid={T.addRuleBtn}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press transition-colors duration-200"
        >
          <Plus className="h-4 w-4" strokeWidth={2} /> Add rule
        </button>
      </header>

      <div className="p-6 lg:p-10 space-y-6">
        {loading ? (
          <div className="text-sm text-[#52525B]">Loading…</div>
        ) : rules.length === 0 ? (
          <div className="border co-hairline bg-white p-10 text-sm text-[#52525B]">
            No reminder rules yet. Click <span className="font-semibold">Add rule</span> to create one.
          </div>
        ) : (
          rules.map((r) => (
            <RuleCard key={r.id} rule={r} onSave={save} onDelete={remove} saving={saving} />
          ))
        )}

        <div className="border co-hairline bg-white p-6 lg:p-8">
          <div className="co-label mb-3">How segments work</div>
          <p className="text-sm text-[#52525B] max-w-2xl">
            Tag clients with segments like <span className="font-mono">gym-premium</span> or{" "}
            <span className="font-mono">mf-vip</span> on the Clients page. Then set a rule&apos;s{" "}
            <span className="font-semibold">Applies to segments</span> to target just that group with
            a custom cadence and message. Leave the field blank to apply to every client.
          </p>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">Add reminder rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <Label className="co-label flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-[#4338CA]" strokeWidth={2} />
                Start from a template (optional)
              </Label>
              <Select value={newRule.templateId} onValueChange={applyTemplate}>
                <SelectTrigger className="mt-2" data-testid={T.templatePicker}>
                  <SelectValue placeholder="Choose an industry template…" />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`${T.templateOption}-${t.id}`}>
                      {t.category} — {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newRule.templateId && (
                <div className="mt-2 text-xs text-[#52525B] border co-hairline bg-[#FAFAFA] p-2.5 rounded-sm font-mono whitespace-pre-wrap max-h-[120px] overflow-auto">
                  {newRule.template}
                </div>
              )}
            </div>
            <div>
              <Label className="co-label">Name</Label>
              <Input
                value={newRule.name}
                onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                className="mt-2"
                placeholder="e.g. Gym VIP reminders"
                required
              />
            </div>
            <div>
              <Label className="co-label">Days before due</Label>
              <Input
                value={newRule.daysStr}
                onChange={(e) => setNewRule({ ...newRule, daysStr: e.target.value })}
                className="mt-2 font-mono"
                placeholder="7, 3, 0"
              />
            </div>
            <div>
              <Label className="co-label">Applies to segments (empty = all)</Label>
              <Input
                value={newRule.segStr}
                onChange={(e) => setNewRule({ ...newRule, segStr: e.target.value })}
                className="mt-2"
                placeholder="gym-premium, vip"
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm font-semibold border border-[#0A0A0B] rounded-sm co-btn-press hover:bg-[#F4F4F5]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold bg-[#4338CA] hover:bg-[#3730A3] text-white rounded-sm co-btn-press disabled:opacity-60 transition-colors duration-200"
              >
                {saving ? "Creating…" : "Create rule"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
